<script setup lang="ts">
import AppLockedScreen from "@/components/ui/AppLockedScreen.vue";
import FirstLoginModal from "@/components/ui/FirstLoginModal.vue";
import GlobalNotification from "@/components/ui/GlobalNotification.vue";
import HtmlGenerationLoader from "@/components/ui/HtmlGenerationLoader.vue";
import Notification from "@/components/ui/Notification.vue";
import RateLimitModal from "@/components/ui/RateLimitModal.vue";
import RgpdBubble from "@/components/ui/RgpdBubble.vue";
import EmailMissingModal from "@/components/dialogs/EmailMissingModal.vue";
import UpdateNotification from "@/components/UpdateNotification.vue";
import { useHtmlGenerationLoader } from '@/composables/ui/useHtmlGenerationLoader';
import { useBrandColors } from '@/composables/useBrandColors';
import { useTermsAcceptance } from '@/composables/useTermsAcceptance';
import SessionProvider from "@/providers/SessionProvider.vue";
import { useAuthStore } from "@/stores/auth";
import { useConfigStore } from "@/stores/config";
import { useRateLimitStore } from "@/stores/ui/rateLimit";
import { onMounted, onUnmounted, watch } from '@vue/runtime-core';

// Déclarer les types pour les propriétés globales de window
declare global {
  interface Window {
    BrevoConversationsID: string;
    BrevoConversations: any;
  }
}

const authStore = useAuthStore();
const configStore = useConfigStore();
const rateLimitStore = useRateLimitStore();
const {
  showFirstLoginModal,
  initTermsCheck,
  handleFirstLoginModalClose,
  handleFirstLoginModalValidate
} = useTermsAcceptance();

const { isLoaderVisible } = useHtmlGenerationLoader();

// Initialiser les couleurs de marque
const { applyBrandClassToBody } = useBrandColors();

// Surveiller les changements d'authentification
watch(() => authStore.user, (user: any) => {
  if (user) {
    initTermsCheck(user);
    // Charger la config quand l'utilisateur est connecté
    configStore.fetchAgcoConfig();
  }
}, { immediate: true });

// Écouter les événements de rate limiting
const handleRateLimitEvent = (event: Event) => {
  console.log('[APP] Rate limit event received:', event);
  const customEvent = event as CustomEvent;
  const { message, duration } = customEvent.detail;
  console.log('[APP] Opening modal with message:', message, 'duration:', duration);
  rateLimitStore.showRateLimitModal(message, duration);
};

onMounted(() => {
  console.log('[APP] Adding rate-limit-exceeded listener');
  window.addEventListener('rate-limit-exceeded', handleRateLimitEvent);
});

onUnmounted(() => {
  console.log('[APP] Removing rate-limit-exceeded listener');
  window.removeEventListener('rate-limit-exceeded', handleRateLimitEvent);
});

// (function (d: Document, w: Window, c: string) {
//   w.BrevoConversationsID = "669fa942b284df0f723c100c";
//   (w as any)[c] =
//     (w as any)[c] ||
//     function () {
//       ((w as any)[c].q = (w as any)[c].q || []).push(arguments);
//     };
//   var s = d.createElement("script");
//   s.async = true;
//   s.src = "https://conversations-widget.brevo.com/brevo-conversations.js";
//   if (d.head) d.head.appendChild(s);
// })(document, window, "BrevoConversations");
</script>

<template>
  <!-- Écran de blocage de l'application -->
  <AppLockedScreen
    v-if="configStore.isAppLocked"
    :message="configStore.appLockedMessage"
  />
  
  <!-- Application normale -->
  <template v-else>
    <GlobalNotification />
    <Notification />
    <UpdateNotification />
    <FirstLoginModal
      :show="showFirstLoginModal"
      @close="handleFirstLoginModalClose"
      @validate="handleFirstLoginModalValidate"
    />
    <RateLimitModal
      :is-open="rateLimitStore.isModalOpen"
      :message="rateLimitStore.message"
      :duration="rateLimitStore.duration"
      @close="rateLimitStore.closeRateLimitModal"
      @timeout="rateLimitStore.handleTimeout"
    />
    <EmailMissingModal
      :show="authStore.emailMissing"
      @close="authStore.emailMissing = false"
    />
    <RgpdBubble />
    <HtmlGenerationLoader :is-visible="isLoaderVisible" />
    <SessionProvider :user="authStore.user">
      <div id="app">
        <router-view />
      </div>
    </SessionProvider>
  </template>
</template>

<style>
@import "@/assets/main.css";
</style>
